import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { PartCatalogSummaryDto, RobotPartDto } from '@bhops/shared';
import { partsApi } from '@/api/parts';
import { extractApiError } from '@/api/client';
import { kstYmd } from '@/lib/format-date';
import { Icon } from '@/components/Icon';
import { PartCatalogCreateModal } from '../parts/PartCatalogCreateModal';
import { PartInstallModal } from './PartInstallModal';
import { CATEGORY_LABEL } from './part-slots';
import {
  AddButton,
  PickList,
  RobotStage,
  SideColumn,
  boardGridStyle,
  cardStyle,
  emptyStyle,
  panelStyle,
  removeBtnStyle,
  type PickItem,
  type RobotStageHandle,
} from './robot-board';

// 2026-10-28 오너 — 로봇 상세 '부품' 탭의 부품 장착 보드.
//   왼쪽: 장착 가능한 부품(활성 카탈로그, 로더 제외). 이미 장착된 것은 '장착됨 · 슬롯' 표시.
//   가운데: 로봇 그림 — 고른 부품을 끌어다 놓으면 장착 확인 창.
//   오른쪽: 지금 장착된 부품.
// 고르기 · 끌기 · 로봇 그림 · 움직임은 robot-board.tsx 의 공용 조각이다(적용 변경점 보드와 같음).

export const PART_DRAG_TYPE = 'application/x-bhops-part-catalog-ids';

export function PartInstallBoard({
  robotId,
  active,
  canSwap,
  onRemove,
}: {
  robotId: string;
  active: RobotPartDto[];
  canSwap: boolean;
  onRemove: (part: RobotPartDto) => void;
}) {
  const [installing, setInstalling] = useState<PartCatalogSummaryDto[]>([]);
  const [dragging, setDragging] = useState(false);
  // 새 부품 등록(같은 날 오너 "부품 등록하는 버튼도 만들어야해") — 부품 카탈로그 화면이 메뉴에서
  // 숨겨져 있어(nav-hidden.ts) 여기서 바로 등록한다. 등록하면 목록이 새로 온다.
  // 권한은 장착과 같다(서버 parts.controller · robot-parts.controller 모두 OPERATIONS_ROLES).
  const [createOpen, setCreateOpen] = useState(false);
  // 방금 장착된 카드 — 들어오는 움직임과 잠깐의 빛을 준다.
  const [fresh, setFresh] = useState<string[]>([]);
  const stageRef = useRef<RobotStageHandle>(null);

  const catalogQ = useQuery({
    queryKey: ['parts', 'catalog', { active: true }],
    queryFn: () => partsApi.listCatalog({ active: true }),
    enabled: canSwap,
    staleTime: 60_000,
  });
  // 로더는 전용 '로더' 탭에서 관리 — 여기서는 뺀다(docs/plan/loader-history-traceability-2026-07 §4.2).
  const catalogs = useMemo(
    () => (catalogQ.data ?? []).filter((c) => c.category !== 'LOADER'),
    [catalogQ.data],
  );

  // 이 로봇에 지금 끼워진 부품(카탈로그 → 슬롯들) — 왼쪽 목록에 '장착됨'으로 보인다.
  const installedSlots = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of active) m.set(p.partCatalog.id, [...(m.get(p.partCatalog.id) ?? []), p.slot]);
    return m;
  }, [active]);

  const items = useMemo<PickItem[]>(
    () =>
      catalogs.map((c) => {
        const slots = installedSlots.get(c.id);
        return {
          id: c.id,
          title: c.displayName,
          meta: `${CATEGORY_LABEL[c.category]} · 재고 ${c.inventory?.usableRemaining ?? '-'}`,
          searchText: c.sku,
          badge: slots ? `장착됨 · ${slots.join(', ')}` : null,
          hint: slots
            ? `이 로봇에 이미 장착되어 있습니다(${slots.join(', ')}). 다시 놓으면 같은 슬롯은 새 부품으로 바뀝니다.`
            : '로봇 그림에 끌어다 놓으면 장착합니다',
        };
      }),
    [catalogs, installedSlots],
  );

  useEffect(() => {
    if (fresh.length === 0) return;
    const t = window.setTimeout(() => setFresh([]), 2400);
    return () => window.clearTimeout(t);
  }, [fresh]);

  function openInstall(ids: string[]): void {
    const picked = catalogs.filter((c) => ids.includes(c.id));
    if (picked.length > 0) setInstalling(picked);
  }

  return (
    <div>
      <div style={boardGridStyle(canSwap)}>
        {canSwap && (
          <PickList
            title="장착 가능한 부품"
            items={items}
            loading={catalogQ.isLoading}
            error={catalogQ.error ? extractApiError(catalogQ.error) : null}
            emptyText="등록된 부품이 없습니다."
            searchPlaceholder="이름, SKU, 분류로 찾기"
            dragType={PART_DRAG_TYPE}
            icon="component"
            actionVerb="장착"
            badgeLabel="장착됨"
            testIdPrefix="part"
            toolbar={
              <AddButton
                label="부품 등록"
                onClick={() => setCreateOpen(true)}
                testId="part-catalog-create"
              />
            }
            onDragState={setDragging}
            onAction={openInstall}
          />
        )}

        <div style={{ ...panelStyle, display: 'flex', gap: 12, padding: 12 }}>
          <RobotStage
            ref={stageRef}
            robotId={robotId}
            dragType={PART_DRAG_TYPE}
            canDrop={canSwap}
            dragging={dragging}
            hints={{
              idle: '왼쪽 부품을 로봇에 끌어다 놓으면 장착합니다',
              ready: '여기로 가져오세요',
              over: '놓으면 장착합니다',
              denied: '부품 장착 권한이 없습니다.',
            }}
            nameOf={(id) => catalogs.find((c) => c.id === id)?.displayName}
            onDropIds={(ids) => {
              setDragging(false);
              openInstall(ids);
            }}
            testId="robot-drop-zone"
          />
          <SideColumn title="장착된 부품" count={active.length}>
            {active.length === 0 ? (
              <div style={emptyStyle}>장착된 부품이 없습니다.</div>
            ) : (
              active.map((p) => (
                <ActivePartCard
                  key={p.id}
                  part={p}
                  canEdit={canSwap}
                  freshIndex={fresh.indexOf(p.id)}
                  onRemove={() => onRemove(p)}
                />
              ))
            )}
          </SideColumn>
        </div>
      </div>

      <PartInstallModal
        catalogs={installing}
        onClose={() => setInstalling([])}
        robotId={robotId}
        activeParts={active}
        onInstalled={(ids) => {
          setFresh(ids);
          stageRef.current?.burst();
        }}
      />
      <PartCatalogCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

// ── 오른쪽: 장착된 부품 카드 ──

function ActivePartCard({
  part,
  canEdit,
  freshIndex,
  onRemove,
}: {
  part: RobotPartDto;
  canEdit: boolean;
  /** 방금 장착된 순번(아니면 -1) — 들어오는 움직임을 차례로 준다. */
  freshIndex: number;
  onRemove: () => void;
}) {
  const isDefective =
    part.partLot &&
    (part.partLot.defectStatus === 'SUSPECTED' || part.partLot.defectStatus === 'CONFIRMED');
  const fresh = freshIndex >= 0;
  return (
    <div
      data-testid={`active-part-${part.id}`}
      className={fresh ? 'pib-card-new' : undefined}
      style={{
        ...cardStyle,
        borderColor: isDefective ? 'var(--severity-urgent)' : 'var(--border-default)',
        animationDelay: fresh ? `${freshIndex * 60}ms` : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            fontSize: 11,
            color: 'var(--fg-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {part.slot}
        </span>
        <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          {CATEGORY_LABEL[part.partCatalog.category]}
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{part.partCatalog.displayName}</div>
      <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--fg-muted)' }}>
        SKU {part.partCatalog.sku}
        {part.serialNo && ` · SN ${part.serialNo}`}
      </div>
      {part.partLot && (
        <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>
          LOT{' '}
          <Link
            to={`/parts/lots/${part.partLot.id}`}
            style={{
              color: isDefective ? 'var(--severity-urgent)' : 'var(--blue-500)',
              textDecoration: 'none',
              fontWeight: isDefective ? 700 : 500,
            }}
          >
            {part.partLot.lotCode}
          </Link>
          {isDefective && (
            <span
              style={{
                color: 'var(--severity-urgent)',
                fontWeight: 700,
                marginLeft: 6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icon name="alert" size={14} aria-hidden /> {part.partLot.defectStatus}
            </span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
          장착 {kstYmd(part.installedAt) ?? '-'}
          {part.warrantyExpiresAt && ` · 보증 ${kstYmd(part.warrantyExpiresAt) ?? '-'}`}
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={onRemove}
            data-testid={`part-remove-${part.id}`}
            style={removeBtnStyle}
          >
            제거
          </button>
        )}
      </div>
    </div>
  );
}
